import {useProtocolDefinitionsStore} from "../store/protocolDefinitions";
import React, {useEffect, useState} from "react";
import {useNeurotuneStore} from "@/common/store/neurotune.ts";
import {IconEdit, IconPlus, IconSearch, IconTrash} from '@tabler/icons-react';
import {
    Affix,
    Box,
    BoxProps,
    Button,
    Loader,
    Text,
    TextInput,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import classes from "./ProtocolDefinitionDashboardPage.module.css"
import commonClasses from "../../common/common.module.css"
import cx from "clsx";
import {IProtocolDefinition} from "../types/protocolDefinitions";
import {useNavigate} from "react-router";
import * as protocolService from "../services/protocols"
import {dayjsToDisplayString} from "@/common/utils/times.ts";
import dayjs from "dayjs";
import {openConfirmModal} from "../modals";


export interface ProtocolDefinitionDashboardPageProps extends BoxProps {

}

export const ProtocolDefinitionDashboardPage: React.FC<ProtocolDefinitionDashboardPageProps> = ({...others}: ProtocolDefinitionDashboardPageProps) => {
    const navigate = useNavigate()
    const activeCompany = useNeurotuneStore((state) => state.activeCompany)
    const protocolDefinitionsLoading = useProtocolDefinitionsStore((state) => state.protocolDefinitionsLoading)
    const loadProtocolDefinitions = useProtocolDefinitionsStore((state) => state.loadProtocolDefinitions)
    const protocolDefinitions = useProtocolDefinitionsStore((state) => state.protocolDefinitions)
    const addProtocolDefinition = useProtocolDefinitionsStore((state) => state.addProtocolDefinition)
    const deleteProtocolDefinition = useProtocolDefinitionsStore((state) => state.deleteProtocolDefinition)
    const [_saving, setSaving] = useState(false)
    const [filterString, setFilterString] = useState<string>()

    const onNewProtocol = async () => {
        const name = `New Protocol ${protocolDefinitions?.length || 0}`
        const instructions = ""
        setSaving(true)
        const newProtocol = await protocolService.createProtocolDefinition(activeCompany.id, {
            name,
            instructions
        })
        addProtocolDefinition(activeCompany.id, newProtocol)
        setSaving(false)
    }
    const onDeleteProtocol = (protocol: IProtocolDefinition) => {
        openConfirmModal({
            size: "md",
            children: <Text>Are you sure you wish to delete {protocol.name} </Text>,
            onConfirm: () => {
                setSaving(true)
                protocolService.deleteProtocolDefinition(activeCompany.id, protocol.id).then(() =>
                    deleteProtocolDefinition(activeCompany.id, protocol.id)
                ).finally(() => {
                    setSaving(false)
                })
            }
        })

    }

    useEffect(() => {
        loadProtocolDefinitions(activeCompany.id)
    }, [])

    const renderLoadingSavingStatus = () => {
        return (
            <Affix position={{bottom: 20, right: 20}}>
                {protocolDefinitionsLoading || protocolDefinitionsLoading ?
                    <Loader color="var(--bios-strong-accent)"/> : <></>}
            </Affix>
        )
    }

    const renderRows = () => protocolDefinitions.filter((pd) =>
        !filterString || pd.name?.toLowerCase().indexOf(filterString.toLowerCase()) >= 0 ||
        pd.id.toString().toLowerCase().indexOf(filterString.toLowerCase()) >= 0
    ).map((row: IProtocolDefinition) => (
        <Box key={row.id} className={cx(commonClasses.hover, classes.row)}>
            <Box className={cx(classes.rowActions)}>
                <Tooltip label={"Delete Protocol Definition"}>
                    <UnstyledButton mr={"0.5em"} size={"compact-xs"} onClick={(ev) => {
                        ev.stopPropagation()
                        onDeleteProtocol(row)
                    }}>
                        <IconTrash className={cx(classes.iconDelete, classes.icon)}/>
                    </UnstyledButton>
                </Tooltip>
                <Tooltip label={"Edit Protocol Definition"}>
                    <UnstyledButton mr={"0.5em"} size={"compact-xs"}
                                    onClick={() => navigate(`/planning/pdef/${row.id}`)}>
                        <IconEdit className={cx(classes.iconEdit, classes.icon)}/>
                    </UnstyledButton>
                </Tooltip>
            </Box>
            <Box>
                {row.id}
            </Box>
            <Box>
                {row.name}
            </Box>
            <Box>{row.step_ids?.length}</Box>
            <Box>
                {dayjsToDisplayString(dayjs(row.createdAt))}
            </Box>
        </Box>
    ));

    return <Box className={cx(classes.root, commonClasses.sbRadius)} {...others}>
        <Box mr={"0.5rem"} ml={"0.25rem"}>
            <Box h={"var(--bios-toolbar-height)"} className={cx(classes.toolbar)}>
                <TextInput placeholder={"search protocols"} rightSection={<IconSearch size={"1em"}/>}
                           onChange={(ev) => setFilterString(ev.currentTarget.value)}/>
                <Box/>
                <Button style={{alignSelf: "end"}} m={"0.5em"} size={"sm"} onClick={onNewProtocol}
                        leftSection={<IconPlus size={"1em"}/>}
                >New
                    Protocol</Button>
            </Box>
            <Box className={cx(classes.table, commonClasses.tgtBorder)} mb={"1em"}>
                <Box className={cx(classes.header, commonClasses.blur)}>
                    <Box className={cx(classes.headerRow, commonClasses.tgbBorder)}>
                        <Box className={cx(classes.rowActions)}></Box>
                        <Box><Text fw={500} fz="sm">Protocol Id</Text></Box>
                        <Box><Text fw={500} fz="sm">Protocol Definition Name</Text></Box>
                        <Box><Text fw={500} fz="sm">Step Count</Text></Box>
                        <Box><Text fw={500} fz="sm">Creation Date</Text></Box>
                    </Box>
                </Box>
                <Box className={cx(classes.scrollArea)}>
                    {renderRows()}
                </Box>
                <Box
                    className={cx(classes.footer, commonClasses.strongBlur, commonClasses.tgtBorder)}>
                    <Text ta={"end"}>
                        {protocolDefinitions.length} Protocols
                    </Text>
                </Box>
            </Box>
        </Box>
        {renderLoadingSavingStatus()}
    </Box>
}